import type React from 'react';
import type { ElementType } from 'react';
import { twMerge } from 'tailwind-merge';
import type * as TF from 'type-fest';

import Box from '#/ui/components/box';
import type { OverridableStringUnion, OverrideProps } from '#/ui/utils/types';

export type CardPropsVariantOverrides = TF.EmptyObject;

export type CardPropsBackgroundOverrides = TF.EmptyObject;

export interface CardOwnProps {
  /**
   * The content of the component.
   */
  children?: React.ReactNode;
  /**
   * The variant to use.
   * @default 'border'
   */
  variant?: OverridableStringUnion<'flat' | 'border', CardPropsVariantOverrides>;
  /**
   * The background color to use.
   * @default 'base'
   */
  background?: OverridableStringUnion<
    'base' | 'transparent' | 'black',
    CardPropsBackgroundOverrides
  >;
  /**
   * If `true`, the card will be interactive.
   * @default false
   */
  interactive?: boolean;
  /**
   * If `true`, the card will be disabled.
   * @default false
   */
  disabled?: boolean;
}

export type CardTypeMap<
  AdditionalProps = TF.EmptyObject,
  RootComponent extends React.ElementType = 'div',
> = {
  props: AdditionalProps & CardOwnProps;
  defaultComponent: RootComponent;
};

export type CardProps<
  RootComponent extends React.ElementType = CardTypeMap['defaultComponent'],
  AdditionalProps = TF.EmptyObject,
  // @ts-expect-error
> = OverrideProps<CardTypeMap<AdditionalProps, RootComponent>, RootComponent> & {
  component?: React.ElementType;
};

function Card<C extends ElementType = 'div'>(
  {
    component = 'div',
    children,
    variant = 'border',
    background = 'base',
    interactive = false,
    disabled = false,
    className,
    ...props
  }: CardProps<C>,
  ref?: React.ForwardedRef<C>
) {
  const cardClasses = twMerge(
    'gooey-card',
    'd-card',
    // Variant styles
    variant === 'border' && 'border border-base-content/10',
    // Background styles
    background === 'base' && 'bg-background-paper',
    background === 'black' && 'bg-background-default',
    background === 'transparent' && 'bg-transparent',
    // Interactive styles
    interactive && background === 'base' && 'hover:bg-background-paper/90 cursor-pointer',
    interactive && background === 'black' && 'hover:bg-background-default/90 cursor-pointer',
    interactive && background === 'transparent' && 'hover:bg-background-paper/10 cursor-pointer',
    // Disabled state
    disabled && 'opacity-50 pointer-events-none',
    className
  );

  return (
    <Box component={component} ref={ref} className={cardClasses} {...props}>
      {children}
    </Box>
  );
}

Card.displayName = 'GooeyCard';

export default Card;
